import React, { useMemo } from "react";
import { Dialog, DialogTitle, DialogContent, IconButton, Box, Typography, Chip, Divider } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import type { GradeSubjectsBucket } from "./GradeSubjectsSummary";

// Grade order used across the app for one-off consumers that don't get an
// explicit `order_number` from the backend. Backend-provided sort_order
// wins; this is the tie-break / fallback.
const CANONICAL_GRADE_ORDER = [
  "pre-k", "prek", "pre k",
  "jk", "junior kindergarten",
  "sk", "senior kindergarten",
  "1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "11", "12",
  "adult",
] as const;
const canonicalGradeRank = (name: string): number => {
  const n = String(name || "").trim().toLowerCase();
  const direct = CANONICAL_GRADE_ORDER.indexOf(n as any);
  if (direct >= 0) return direct;
  const stripped = n.replace(/^grade\s*/, "");
  const asIdx = CANONICAL_GRADE_ORDER.indexOf(stripped as any);
  return asIdx >= 0 ? asIdx : 999;
};

const labelForGrade = (name: string): string =>
  /^(pre-?k|jk|sk|adult|\d+)$/i.test(String(name).trim()) ? `Grade ${name}` : name;

// A grade name is "numeric-like" when the compact summary can safely
// render it as a range endpoint (e.g. "Grade 3", "Grade 4"). Pre-K / JK /
// SK / Adult are non-numeric anchors and break range collapsing.
const numericGradeValue = (name: string): number | null => {
  const n = String(name || "").trim();
  return /^\d+$/.test(n) ? parseInt(n, 10) : null;
};

// One-line compact summary for cards. Prefers a numeric range when every
// grade is a whole number and the sequence is contiguous ("Grades 2–7"),
// else lists each grade label ("Pre-K, JK, Grade 2, Grade 5"). Total
// mapping count is the sum of subjects across every grade (post-dedup by
// the caller).
export const summarizeGradesCompact = (groups: GradeSubjectsBucket[]): {
  gradesLabel: string;
  mappingsCount: number;
} => {
  if (!groups.length) return { gradesLabel: "", mappingsCount: 0 };
  const sorted = [...groups].sort((a, b) => {
    const oa = a.grade?.sort_order ?? null;
    const ob = b.grade?.sort_order ?? null;
    if (oa != null && ob != null && oa !== ob) return oa - ob;
    return canonicalGradeRank(a.grade.name) - canonicalGradeRank(b.grade.name);
  });
  const mappingsCount = sorted.reduce((n, g) => n + g.subjects.length, 0);
  const numericValues = sorted.map((g) => numericGradeValue(g.grade.name));
  const allNumeric = numericValues.every((v) => v !== null) && sorted.length >= 2;
  if (allNumeric) {
    const nums = (numericValues as number[]).slice().sort((a, b) => a - b);
    const contiguous = nums.every((v, i) => i === 0 || v === nums[i - 1] + 1);
    if (contiguous) {
      return { gradesLabel: `Grades ${nums[0]}–${nums[nums.length - 1]}`, mappingsCount };
    }
  }
  const parts = sorted.map((g) => labelForGrade(g.grade.name));
  return { gradesLabel: parts.join(", "), mappingsCount };
};

interface Props {
  open: boolean;
  onClose: () => void;
  groups: GradeSubjectsBucket[];
  title?: string;
  subtitle?: string;
}

const GradeSubjectsDialog: React.FC<Props> = ({ open, onClose, groups, title = "Subjects by Grade", subtitle }) => {
  const sorted = useMemo(() => {
    const cleaned = groups.map((g) => {
      const seen = new Set<number>();
      const subjects = g.subjects.filter((s) => {
        if (seen.has(s.id)) return false;
        seen.add(s.id);
        return true;
      });
      const orderedSubjects = [...subjects].sort((a, b) => {
        const oa = a.sort_order ?? null;
        const ob = b.sort_order ?? null;
        if (oa != null && ob != null && oa !== ob) return oa - ob;
        return String(a.name).localeCompare(String(b.name));
      });
      return { ...g, subjects: orderedSubjects };
    });
    return cleaned.sort((a, b) => {
      const oa = a.grade?.sort_order ?? null;
      const ob = b.grade?.sort_order ?? null;
      if (oa != null && ob != null && oa !== ob) return oa - ob;
      return canonicalGradeRank(a.grade.name) - canonicalGradeRank(b.grade.name);
    });
  }, [groups]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle sx={{
        pr: 6, pb: subtitle ? 0.5 : 2,
        fontWeight: 800, color: "#1b3a6b", fontSize: 18,
      }}>
        {title}
        <IconButton
          onClick={onClose}
          aria-label="Close"
          sx={{ position: "absolute", right: 12, top: 12, color: "#64748b" }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
        {subtitle && (
          <Typography sx={{ mt: 0.5, fontWeight: 500, fontSize: 12.5, color: "#64748b" }}>
            {subtitle}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent sx={{ pt: 1.5 }}>
        {sorted.length === 0 ? (
          <Typography sx={{ color: "#64748b", fontSize: 13, py: 2 }}>
            No grade-subject mappings for this session.
          </Typography>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {sorted.map((g, i) => (
              <Box key={g.grade_id ?? i}>
                <Typography sx={{
                  fontWeight: 800, color: "#1b3a6b", fontSize: 14, letterSpacing: 0.2,
                  mb: 0.75,
                }}>
                  {labelForGrade(g.grade.name)}
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                  {g.subjects.map((s) => (
                    <Chip
                      key={s.id}
                      label={s.name}
                      size="small"
                      sx={{
                        bgcolor: "#eff6ff", color: "#1b3a6b",
                        border: "1px solid #dbeafe", fontWeight: 600,
                        borderRadius: 1.5,
                      }}
                    />
                  ))}
                </Box>
                {i < sorted.length - 1 && <Divider sx={{ mt: 2, borderColor: "#f1f5f9" }} />}
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default GradeSubjectsDialog;
